/**
 * ORO MVP API Client
 *
 * The HTTP transport primitives (`API_BASE_URL`, `ApiError`, `ApiRequestOptions`,
 * and the `apiGet`/`apiPost`/`apiPatch`/`apiPut`/`apiDelete` verb helpers) live in
 * `./apiTransport` and are re-exported here. That module is platform-specific:
 * the browser uses cookie sessions; a sibling `apiTransport.native.ts` provides
 * the React Native (Expo) Bearer-token transport (Metro auto-prefers `.native.ts`).
 * Re-exporting keeps every existing `import { apiPost } from '../api'` working
 * unchanged, while the `*Api` objects and domain modules below — which call these
 * helpers — become transport-agnostic across web and native.
 */
export {
  API_BASE_URL,
  ApiError,
  apiGet,
  apiPost,
  apiPostPublic,
  apiPatch,
  apiPut,
  apiDelete,
} from './apiTransport';
export type { ApiRequestOptions } from './apiTransport';

import { apiGet, apiPost, apiPatch, apiPut, apiDelete, API_BASE_URL, ApiError } from './apiTransport';
import { guardedFetch } from './accountGuard';
import type { CheckinResponse } from './api/events';

// Identity System Types (v0.8.0)
export const UserLevel = {
  FREE: 'free',
  RUNNER: 'runner',
  CLUB_LEADER: 'club_leader',
  FOUNDER: 'founder'
} as const;

// #1777: Aligned with backend canonical tier set (clubs.subscription_tier
// CHECK constraint in 0001_initial_schema.sql) and SubscriptionTier in
// frontend/src/lib/api/clubs.ts. Use SubscriptionTier for new code; this
// constant is retained as a value alias for legacy consumers.
export const SubscriptionLevel = {
  FREE: 'free',
  ESSENTIAL: 'essential',
  ADVANCED: 'advanced',
} as const;

export const ClubRole = {
  OWNER: 'owner',
  MEMBER: 'member'
  // Future: ADMIN, MODERATOR, etc.
} as const;

export type UserLevel = typeof UserLevel[keyof typeof UserLevel];
export type SubscriptionLevel = typeof SubscriptionLevel[keyof typeof SubscriptionLevel];
export type ClubRole = typeof ClubRole[keyof typeof ClubRole];

// Type definitions for API responses
export interface User {
  id: string;
  email: string;
  // Identity & Naming (v0.8.0)
  public_display_name: string;
  first_name: string;
  last_name: string;
  public_name_mode: 'full' | 'first_last_initial' | 'first_only' | 'anonymous';
  // Legacy field - maintained for compatibility
  name: string | null;
  avatar_url: string | null;
  // Profile fields (v0.7.0)
  tagline: string | null;
  birthday_day: number | null;
  birthday_month: number | null;
  location: string | null;
  primary_sport: string | null;
  secondary_sports: string[] | null;
  profile_image_url: string | null;
  // Identity system (v0.8.0)
  // user_level removed - see #785
  subscription_level: SubscriptionLevel | null;
  created_at: number;
  updated_at: number;
  // Returned by formatUserForResponse on every sign-in response (consume, SMS
  // verify, reviewer login). Required so response drift fails typechecking.
  onboarding_completed: boolean;
}

// PublicUserProfile moved to lib/api/users.ts (#2253 domain-module extraction).

export interface MagicLinkRequestResponse {
  ok: true;
  // #2345: set only for the armed App Review demo account — the client shows a
  // static-code entry step instead of "check your email" (the reviewer can't
  // read our email). Absent for all normal users.
  reviewer_code_entry?: boolean;
}

export interface MagicLinkConsumeResponse {
  user: User;
  redirect_url?: string; // Cross-platform redirect (#830)
}

export interface RegistrationRequest {
  first_name: string;
  last_name: string;
  public_name_mode?: 'full' | 'first_last_initial' | 'first_only' | 'anonymous';
}

export interface RegistrationResponse {
  user: User;
}

export interface CurrentUserResponse {
  user: User;
}


export interface Event {
  id: string;
  club_id: string;
  club_slug?: string;
  title: string;
  description: string | null;
  starts_at: number;
  ends_at: number | null;
  location_name: string | null;
  location_address: string | null;
  location_lat: number | null;
  location_lng: number | null;
  checkin_radius_m: number | null;
  checkin_window_before_min: number | null;
  checkin_window_after_min: number | null;
  distance_m: number | null;
  is_recurring: number;
  owner_id: string;
  created_at: number;
  updated_at: number;
}

export interface EventDetailResponse {
  event: Event;
}

export interface CreateEventRequest {
  title: string;
  description?: string;
  starts_at: number;
  ends_at?: number;
  location_name?: string;
  location_address?: string;
  location_lat?: number;
  location_lng?: number;
  checkin_radius_m?: number;
  checkin_window_before_min?: number;
  checkin_window_after_min?: number;
  distance_m?: number;
}

export interface CreateEventResponse {
  event: Event;
}

export interface UpdateEventRequest {
  title?: string;
  description?: string;
  starts_at?: number;
  ends_at?: number;
  location_name?: string;
  location_address?: string;
  location_lat?: number;
  location_lng?: number;
  checkin_radius_m?: number;
  checkin_window_before_min?: number;
  checkin_window_after_min?: number;
  distance_m?: number;
}

export interface UpdateEventResponse {
  event: Event;
}

export interface ClubForEventCreation {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  my_role: 'owner' | 'leader';
  member_count: number;
}

export interface ClubsForEventCreationResponse {
  clubs: ClubForEventCreation[];
}

// Auth API calls
export const authApi = {
  // #3312: `terms_accepted` is the login-screen Terms checkbox. NOTE — this
  // module and `lib/api/auth.ts` both define these calls; LoginModal imports
  // THIS one, so a change to only the domain module is a no-op on the live
  // login path. Keep the pair in sync — the dedup is #3316, deliberately not
  // done here (out of scope for #3312).
  requestMagicLink: (email: string, turnstileToken?: string, inviteCode?: string, redirectUrl?: string, termsAccepted?: boolean) =>
    apiPost<MagicLinkRequestResponse>('/auth/magic-link/request', {
      email,
      ...(turnstileToken && { turnstile_token: turnstileToken }),
      ...(inviteCode && { invite_code: inviteCode }),
      ...(redirectUrl && { redirect_url: redirectUrl }),
      ...(termsAccepted && { terms_accepted: true }),
      // #1647: timezone is sent on /magic-link/consume (auth-proven), not here.
    }),

  // Issue #1328: SMS login — sends a one-time code via sendSmsWithConsent (verify: POST /auth/sms/verify)
  requestSmsCode: (phone: string, turnstileToken?: string, inviteCode?: string, redirectUrl?: string, termsAccepted?: boolean) =>
    apiPost<MagicLinkRequestResponse>('/auth/sms/request', {
      phone,
      ...(turnstileToken && { turnstile_token: turnstileToken }),
      ...(inviteCode && { invite_code: inviteCode }),
      ...(redirectUrl && { redirect_url: redirectUrl }),
      ...(termsAccepted && { terms_accepted: true }), // #3312
    }),

  verifySmsCode: (phone: string, code: string) =>
    // #1647: capture browser tz at verify time (this is when the SMS user
    // is actually created in the DB; magic-link's findOrCreateUser handles
    // the email path on the request side instead).
    apiPost<MagicLinkConsumeResponse>('/auth/sms/verify', {
      phone,
      code,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),

  // #2345: App Review demo-account static-code login. Reached only when
  // /auth/magic-link/request returned `reviewer_code_entry` for this email;
  // the backend gate (armed flag + secret + rate limit) does the real work.
  reviewerLogin: (email: string, code: string, termsAccepted?: boolean) =>
    // #3312: the reviewer path never consumes a magic link (the request
    // endpoint returns reviewer_code_entry BEFORE inserting one), so the
    // ticked box cannot ride the magic_links row like it does for everyone
    // else. It rides this call instead and is recorded on success.
    apiPost<MagicLinkConsumeResponse>('/auth/reviewer/login', {
      email,
      code,
      ...(termsAccepted && { terms_accepted: true }),
    }),

  consumeMagicLink: (token: string) =>
    // #1647: tz captured at consume (auth-proven), not at request.
    apiPost<MagicLinkConsumeResponse>('/auth/magic-link/consume', {
      token,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),

  register: (data: RegistrationRequest) =>
    apiPost<RegistrationResponse>('/auth/register', data),

  getCurrentUser: () =>
    apiGet<CurrentUserResponse>('/auth/me'),

  logout: () =>
    apiPost<MagicLinkRequestResponse>('/auth/logout'),
};

// Events API calls
export const eventsApi = {
  getClubsForEventCreation: () =>
    apiGet<ClubsForEventCreationResponse>('/events/clubs-for-creation'),

  getClubEvents: (clubId: string, params?: { from?: number; to?: number }) => {
    const query = new URLSearchParams();
    if (params?.from) query.append('from', params.from.toString());
    if (params?.to) query.append('to', params.to.toString());
    const queryString = query.toString();
    return apiGet<Event[]>(`/clubs/${clubId}/events${queryString ? `?${queryString}` : ''}`);
  },

  getEventById: (eventId: string) =>
    apiGet<EventDetailResponse>(`/events/${eventId}`),

  createEvent: (clubId: string, data: CreateEventRequest) =>
    apiPost<CreateEventResponse>(`/clubs/${clubId}/events`, data),

  updateEvent: (eventId: string, data: UpdateEventRequest) =>
    apiPatch<UpdateEventResponse>(`/events/${eventId}`, data),

  deleteEvent: (eventId: string) =>
    apiDelete<{ ok: true }>(`/events/${eventId}`),
};

// Check-in interfaces
export interface CheckinRequest {
  lat?: number;
  lng?: number;
  source: 'gps' | 'qr' | 'offline' | 'secret_word';
  secret_word?: string;
  // #3698: sent only by the offline-queue sync (the member who queued it). The
  // server refuses the check-in with 403 when it is not the session user.
  expected_user_id?: string;
}

// Profile API Types
export interface ProfileUpdateRequest {
  // Identity & Naming (v0.8.0)
  first_name?: string;
  last_name?: string;
  public_name_mode?: 'full' | 'first_last_initial' | 'first_only' | 'anonymous';
  // Legacy field - maintained for compatibility  
  name?: string;
  tagline?: string;
  birthday_day?: number | null;
  birthday_month?: number | null;
  location?: string;
  primary_sport?: string;
  secondary_sports?: string[];
}

export interface ProfileResponse {
  profile: User;
}

// Check-ins API calls
export const checkinsApi = {
  // #3766: typed with the one CheckinResponse in lib/api/events.ts, which
  // matches the handler on every success path (201 create, 200 dedupe, 200
  // already checked in). The old client `Checkin` type promised lat, lng and
  // created_at, which this endpoint never returns.
  createCheckin: (eventId: string, data: CheckinRequest) =>
    apiPost<CheckinResponse>(`/events/${eventId}/checkins`, data),
};

// Profile API calls
export const profileApi = {
  getProfile: () =>
    apiGet<ProfileResponse>('/profile'),
  updateProfile: (data: ProfileUpdateRequest) =>
    apiPut<ProfileResponse>('/profile', data),
  removeProfileImage: () =>
    apiDelete<{ ok: true }>('/profile/image'),
};

// usersApi moved to lib/api/users.ts (#2253 domain-module extraction).

// Images API - Unified image upload with CF Image Resizing (#1068)
export type ImagesUploadType = 'avatar' | 'hero' | 'chat' | 'profile';

export interface ImagesUploadResponse {
  image: {
    url: string;
    type: ImagesUploadType;
  };
}

export const imagesApi = {
  /**
   * Upload image via unified API with CF Image Resizing
   * Issue #1068 - All image uploads go through this endpoint
   */
  upload: async (file: File, type: ImagesUploadType): Promise<ImagesUploadResponse> => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', type);

    const url = `${API_BASE_URL}/images/upload`;

    const response = await guardedFetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      try {
        const body = await response.json();
        if (body.error) {
          throw new ApiError(
            body.error.message || 'Upload failed',
            body.error.code || 'UPLOAD_ERROR',
            response.status
          );
        }
      } catch (err) {
        if (err instanceof ApiError) throw err;
      }
      throw new ApiError(
        `Upload failed: ${response.status} ${response.statusText}`,
        'UPLOAD_ERROR',
        response.status
      );
    }

    return response.json();
  },
};

// Upload API calls (legacy - prefer imagesApi for new code)
export const uploadApi = {
  uploadClubChatImage: async (clubId: string, file: File): Promise<{ success: boolean; image_url: string; message: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    
    const url = `${API_BASE_URL}/upload/club-chat-image/${clubId}`;
    
    const response = await guardedFetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      try {
        const body = await response.json();
        if (body.error) {
          throw new ApiError(
            body.error.message || 'Chat image upload failed',
            body.error.code || 'UPLOAD_ERROR',
            response.status
          );
        }
      } catch (err) {
        if (err instanceof ApiError) throw err;
      }
      throw new ApiError(
        `Club chat image upload failed: ${response.status} ${response.statusText}`,
        'UPLOAD_ERROR',
        response.status
      );
    }

    return response.json();
  },
  
  removeProfileImage: () =>
    apiDelete<{ success: boolean; message: string }>('/upload/profile-image'),

  uploadClubHeroImage: async (clubId: string, file: File): Promise<{ success: boolean; image_url: string; message: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    
    const url = `${API_BASE_URL}/upload/club-hero-image/${clubId}`;
    
    const response = await guardedFetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      try {
        const body = await response.json();
        if (body.error) {
          throw new ApiError(
            body.error.message || 'Hero image upload failed',
            body.error.code || 'UPLOAD_ERROR',
            response.status
          );
        }
      } catch (err) {
        if (err instanceof ApiError) throw err;
      }
      throw new ApiError(
        `Hero image upload failed: ${response.status} ${response.statusText}`,
        'UPLOAD_ERROR',
        response.status
      );
    }

    return response.json();
  },

  removeClubHeroImage: async (clubId: string): Promise<{ success: boolean; message: string }> => {
    const url = `${API_BASE_URL}/upload/club-hero-image/${clubId}`;
    
    const response = await guardedFetch(url, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      try {
        const body = await response.json();
        if (body.error) {
          throw new ApiError(
            body.error.message || 'Failed to remove hero image',
            body.error.code || 'DELETE_ERROR',
            response.status
          );
        }
      } catch (err) {
        if (err instanceof ApiError) throw err;
      }
      throw new ApiError(
        `Failed to remove hero image: ${response.status} ${response.statusText}`,
        'DELETE_ERROR',
        response.status
      );
    }

    return response.json();
  },
};
